import { convertYYYYMMDDToDate, toEasternDateKey, UNZONED_ISO_DATE_TIME } from './formatDate';

// Tri-Ax runs on Eastern time. The MUI date-fns adapter can't apply a timezone, so pickers work in the device's
// zone. These helpers convert between real instants and "wall" Dates whose device-local fields read as the
// Eastern clock, so pickers show and enter Eastern time on any device.

const EASTERN_PARTS = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
});

const getEasternParts = (instant: Date) => {
    const parts: Record<string, number> = {};
    EASTERN_PARTS.formatToParts(instant).forEach((part) => {
        if (part.type !== 'literal') {
            parts[part.type] = Number(part.value);
        }
    });
    return parts;
};

// Eastern offset from UTC, in ms, at a UTC instant (e.g. -4h during EDT).
const getEasternOffsetMs = (utcMs: number) => {
    const p = getEasternParts(new Date(utcMs));
    const easternAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second);
    return easternAsUtc - (utcMs - (utcMs % 1000));
};

/** A Date whose device-local fields read as the Eastern wall-clock time of the instant. For feeding pickers only. */
export const toEasternWallDate = (instant: Date | string) => {
    if (typeof instant === 'string' && UNZONED_ISO_DATE_TIME.test(instant)) {
        // The API sends DateTimeKind.Unspecified values (e.g. the end-of-year expiration cap) without a zone; they're already Eastern wall time
        return new Date(instant);
    }
    const date = new Date(instant);
    if (isNaN(date.getTime())) {
        // Leave an invalid date for the picker to show as invalid (formatToParts would throw)
        return date;
    }
    const p = getEasternParts(date);
    return new Date(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second, date.getMilliseconds());
};

/** The real instant at which an Eastern clock reads the wall Date's device-local fields. Inverse of toEasternWallDate. */
export const fromEasternWallDate = (wall: Date) => {
    if (isNaN(wall.getTime())) {
        return new Date(NaN);
    }
    const wallAsUtc = Date.UTC(wall.getFullYear(), wall.getMonth(), wall.getDate(), wall.getHours(), wall.getMinutes(), wall.getSeconds(), wall.getMilliseconds());
    // Second pass settles the offset when the first guess lands on the other side of a DST change.
    const firstGuess = wallAsUtc - getEasternOffsetMs(wallAsUtc);
    return new Date(wallAsUtc - getEasternOffsetMs(firstGuess));
};

/** The instant of Eastern midnight on the Eastern day the instant falls on. */
export const easternStartOfDay = (instant: Date | string) => {
    const wall = toEasternWallDate(instant);
    return fromEasternWallDate(new Date(wall.getFullYear(), wall.getMonth(), wall.getDate()));
};

/** Today's Eastern calendar day as a device-local midnight Date, for day selectors that send "yyyy-MM-dd". */
export const easternToday = () => convertYYYYMMDDToDate(toEasternDateKey(new Date()));
